import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { ApplicationContext } from 'src/app/core/models/application-context.model';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { AdminCompanyAutoSelectService } from 'src/app/core/services/admin-company-auto-select.service';
import {
  resolveAdminOrganizationContext,
  shouldRequireCompanySelection,
} from 'src/app/core/utils/admin-company.util';
import { setLaunchRequestedCompanyId } from 'src/app/core/utils/admin-launch-context.util';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

@Component({
  selector: 'app-redirect',
  templateUrl: './app-redirect.component.html',
  styleUrls: ['./app-redirect.component.scss']
})
export class AppRedirectComponent implements OnInit {
  constructor(
    private readonly _router: Router,
    private readonly _authService: AuthenticationService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _appContextService: ApplicationContextService,
    private readonly _adminCompanyAutoSelect: AdminCompanyAutoSelectService) {
  }

  ngOnInit() {
    void this.bootstrapFromHandoff();
  }

  private async bootstrapFromHandoff(): Promise<void> {
    this._localStorageService.authorizationData = null;
    this._appContextService.resetContext();

    const params = this._activatedRoute.snapshot.queryParams;
    const requestedCompanyId = params['companyId']
      ? String(params['companyId']).trim()
      : null;
    const requestedUserId = params['userId']
      ? String(params['userId']).trim()
      : null;

    const hydrated = this._authService.hydrateAuthFromQueryParams({
      token: params['token'],
      refreshToken: params['refreshToken'],
      q: params['q'],
      d: params['d'],
    });

    if (!hydrated) {
      this._authService.clearSessionAndRedirectToLogin({ handoffFailed: true });
      return;
    }

    const sessionReady = await this._authService.ensureSessionReadyAfterHandoff();
    if (!sessionReady) {
      this._authService.clearSessionAndRedirectToLogin({ handoffFailed: true });
      return;
    }

    if (requestedCompanyId) {
      setLaunchRequestedCompanyId(requestedCompanyId);
    }

    const loadContext$ = () =>
      this._appContextService.context.pipe(take(1));

    const loadContextWithRequestedCompany$ = () => {
      if (!requestedCompanyId || !requestedUserId) {
        return loadContext$();
      }

      const verifyRequestedCompany = (context: ApplicationContext) => {
        const activeId =
          context.currentlyLoggedInUser?.companyId?.toLowerCase();
        if (activeId !== requestedCompanyId.toLowerCase()) {
          throw new Error(
            'Failed to scope Admin session to workspace organization'
          );
        }
        return of(context);
      };

      if (this._authService.isIdentityAccessToken()) {
        return loadContext$().pipe(switchMap(verifyRequestedCompany));
      }

      return this._adminCompanyAutoSelect
        .switchToCompanyIds(requestedUserId, requestedCompanyId)
        .pipe(
          switchMap((scoped) => {
            if (!scoped) {
              throw new Error(
                'Failed to scope Admin session to workspace organization'
              );
            }
            return loadContext$();
          }),
          switchMap(verifyRequestedCompany)
        );
    };

    loadContextWithRequestedCompany$()
      .subscribe({
        next: (context) => {
          if (!context?.currentlyLoggedInUser?.id) {
            this._authService.clearSessionAndRedirectToLogin({
              handoffFailed: true,
            });
            return;
          }

          if (
            requestedCompanyId &&
            context.currentlyLoggedInUser.companyId?.toLowerCase() ===
              requestedCompanyId.toLowerCase()
          ) {
            void this._router.navigateByUrl('/');
            return;
          }

          if (
            shouldRequireCompanySelection({
              userCompanies: context.userCompanies,
              requestedCompanyId,
            })
          ) {
            void this._router.navigateByUrl(
              '/select-company?reason=multiple-orgs'
            );
            return;
          }

          const access = resolveAdminOrganizationContext({
            companyId: context.currentlyLoggedInUser.companyId,
            userCompanies: context.userCompanies,
          });
          if (access === 'select-company') {
            this._adminCompanyAutoSelect
              .tryAutoSelectSingleCompany(context, { navigateTo: '/' })
              .subscribe((selected) => {
                if (!selected) {
                  void this._router.navigateByUrl(
                    '/select-company?reason=no-org-context'
                  );
                }
              });
            return;
          }

          void this._router.navigateByUrl('/');
        },
        error: () => {
          this._authService.clearSessionAndRedirectToLogin({
            handoffFailed: true,
          });
        },
      });
  }
}
